import axios from 'axios'
import getGuestToken from './auth'

const PUBLIC_ROUTES = new Set([
  'share',
  'share/verify',
  'share/password',
  'download/shared',
  'download',
  'password',
  'verify/file',
  'file/url',
])

// ─── In-memory access token ──────────────────────────────────────────────────

let accessToken = null

export const setApiToken = token => {
  accessToken = token || null
}

export const getApiToken = () => accessToken

// ─── Allowed API hosts (only attach Bearer token to AC-data API requests) ─────
// Collects ALL REACT_APP_API_* env vars and extracts their hostnames.
// Requests to these hosts get the Bearer token; all others (SSO, external) pass through untouched.

const isDevelopment = process.env.NODE_ENV === 'development'

const AC_DATA_API_HOSTS = new Set(
  Object.entries(process.env)
    .filter(([key]) => key.startsWith('REACT_APP_API_'))
    .map(([, value]) => {
      try {
        return new URL(value).host
      } catch {
        return null
      }
    })
    .filter(Boolean),
)

// Also allow localhost (for microservices during development)
if (isDevelopment) {
  AC_DATA_API_HOSTS.add('localhost')
}

const isAcDataApi = url => {
  if (!url) return false
  try {
    return AC_DATA_API_HOSTS.has(new URL(url).host)
  } catch {
    // relative URLs — assume internal
    return false
  }
}

// ─── Shared guest-token bootstrap ───────────────────────────────────────────

const fetchAndStoreGuestToken = async () => {
  const existingUuid = localStorage.getItem('uuid') || null
  const existingSecret = localStorage.getItem('guestSecret') || null
  const { token: newToken, uuid, guestSecret } = await getGuestToken(existingUuid, existingSecret)
  if (newToken) setApiToken(newToken)
  if (uuid) localStorage.setItem('uuid', uuid)
  if (guestSecret) localStorage.setItem('guestSecret', guestSecret)
  return newToken || null
}

// ─── Request interceptor — attach Bearer token ───────────────────────────────

let guestTokenPromise = null

axios.interceptors.request.use(async config => {
  const requestUrl = config.baseURL ? `${config.baseURL}${config.url || ''}` : config.url

  if (!isAcDataApi(requestUrl)) return config
  // Skip auth endpoints themselves
  if (requestUrl.includes('/auth/')) return config

  // parse the path from the URL to check if it's a public route
  let path = ''
  try {
    const urlObj = new URL(requestUrl)
    path = urlObj.pathname.replace(/^\/+/, '') // remove leading slashes
  } catch {
    // relative URL — use as-is
    path = requestUrl.replace(/^\/+/, '')
  }
  if (isDevelopment) {
    path = path.replace(/^dev\//, '') // remove leading 'dev/' in dev mode
  }

  if (PUBLIC_ROUTES.has(path)) return config

  let token = getApiToken()
  if (token) {
    config.headers = config.headers || {}
    config.headers.Authorization = `Bearer ${token}`
    return config
  }

  // No token — bootstrap guest token (only one in-flight at a time)
  if (!guestTokenPromise) {
    guestTokenPromise = fetchAndStoreGuestToken()
      .catch(e => {
        console.warn('Could not obtain guest token:', e)
        return null
      })
      .finally(() => {
        guestTokenPromise = null
      })
  }

  token = await guestTokenPromise
  if (token) {
    config.headers = config.headers || {}
    config.headers.Authorization = `Bearer ${token}`
  }

  return config
})

// ─── Response interceptor — retry on token-expired errors ────────────────────

const RETRYABLE_AUTH_TYPES = new Set([
  'API_TOKEN_EXPIRED',
  'TOKEN_FIND_FAILED_SYSTEM',
  'TOKEN_IS_NOT_EXIST',
])

const isRetryableAuthError = body => {
  if (!body || body.code !== 'AC_AUTHENTICATE_ERROR') return false
  return RETRYABLE_AUTH_TYPES.has(body.message?.type)
}

let isRefreshing = false
let refreshQueue = []

const processQueue = (error, token = null) => {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error)
    else resolve(token)
  })
  refreshQueue = []
}

const handleTokenExpired = async originalRequest => {
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      refreshQueue.push({ resolve, reject })
    }).then(token => {
      originalRequest.headers.Authorization = `Bearer ${token}`
      return axios(originalRequest)
    })
  }

  originalRequest.retried = true
  isRefreshing = true

  try {
    setApiToken(null)
    const newToken = await fetchAndStoreGuestToken()
    if (newToken) {
      processQueue(null, newToken)
      originalRequest.headers.Authorization = `Bearer ${newToken}`
      return axios(originalRequest)
    }

    processQueue(new Error('Token refresh failed'))
    return Promise.reject(new Error('Token refresh failed'))
  } catch (e) {
    processQueue(e)
    return Promise.reject(e)
  } finally {
    isRefreshing = false
  }
}

axios.interceptors.response.use(
  // Success — check 200 body for auth error code
  res => {
    if (isRetryableAuthError(res?.data)) {
      return handleTokenExpired(res.config)
    }
    return res
  },
  // Error — check for auth error in response body
  async error => {
    const originalRequest = error.config
    const url = originalRequest?.baseURL
      ? `${originalRequest.baseURL}${originalRequest.url || ''}`
      : originalRequest?.url

    if (!isAcDataApi(url)) return Promise.reject(error)
    if (originalRequest?.retried) return Promise.reject(error)

    if (
      isRetryableAuthError(error.response?.data) ||
      error.response?.status === 401 ||
      error.response?.status === 403
    ) {
      return handleTokenExpired(originalRequest)
    }

    return Promise.reject(error)
  },
)

export default axios
